import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getImageVariantUrl } from "@/utils/imageVariant";

/**
 * `useImageVariant` — the original's module 86350, verbatim.
 *
 * Serves the resized Firebase Storage rendition and, when that 404s, falls back
 * to the ORIGINAL url. The fallback is deliberately verified with a `HEAD`
 * request rather than trusting the `error` event, because an error can also
 * mean "offline" or "blocked", and permanently downgrading every image on a
 * flaky connection would be worse than a single broken tile.
 *
 * Three refs guard the check: one so a burst of error events fires a single
 * HEAD, one so a non-404 failure is never retried, and the state flag itself so
 * it never runs after the fallback is already in effect.
 *
 * `fallback` is the last resort when there is no url at all. Passing none — as
 * the About header logo does — yields `undefined`, which is how the original
 * hides the logo for a tenant with no `image_url`.
 */
export default function useImageVariant(
  url: string | undefined,
  size: string,
  fallback?: string,
): { imageSrc: string | undefined; onImageError: (event: { currentTarget?: { src?: string } }) => void } {
  const [useOriginal, setUseOriginal] = useState(false);
  const checking = useRef(false);
  const gaveUp = useRef(false);

  useEffect(() => {
    setUseOriginal(false);
    checking.current = false;
    gaveUp.current = false;
  }, [url, size, fallback]);

  const imageSrc = useMemo(() => {
    const resolved = useOriginal ? (url ?? undefined) : getImageVariantUrl(url, size);
    return resolved || fallback;
  }, [url, size, fallback, useOriginal]);

  const onImageError = useCallback(
    (event: { currentTarget?: { src?: string } }) => {
      if (useOriginal || checking.current || gaveUp.current) return;
      checking.current = true;
      const src = event.currentTarget?.src;
      if (!src) {
        checking.current = false;
        return;
      }
      fetch(src, { method: "HEAD" })
        .then((response) => {
          if (response.status === 404) setUseOriginal(true);
          else gaveUp.current = true;
        })
        .catch(() => {})
        .finally(() => {
          checking.current = false;
        });
    },
    [useOriginal],
  );

  return { imageSrc, onImageError };
}
